import React, { HTMLAttributes, ReactNode, TableHTMLAttributes } from "react";
import { cn } from "@app/lib/utils";

type TableDesktopFrom = "sm" | "lg" | "xl" | "2xl";

const responsiveClasses: Record<
  TableDesktopFrom,
  {
    table: string;
    head: string;
    row: string;
    cell: string;
    label: string;
    value: string;
  }
> = {
  sm: {
    table: "sm:table",
    head: "sm:table-header-group",
    row: "sm:mb-0 sm:table-row sm:rounded-none sm:border-0 sm:border-b sm:bg-transparent sm:p-0 sm:shadow-none sm:hover:bg-muted/60",
    cell: "sm:table-cell sm:min-w-0 sm:rounded-none sm:bg-transparent sm:px-4 sm:py-4 sm:align-middle",
    label: "sm:hidden",
    value: "sm:contents sm:text-start",
  },
  lg: {
    table: "lg:table",
    head: "lg:table-header-group",
    row: "lg:mb-0 lg:table-row lg:rounded-none lg:border-0 lg:border-b lg:bg-transparent lg:p-0 lg:shadow-none lg:hover:bg-muted/60",
    cell: "lg:table-cell lg:min-w-0 lg:rounded-none lg:bg-transparent lg:px-4 lg:py-4 lg:align-middle",
    label: "lg:hidden",
    value: "lg:contents lg:text-start",
  },
  xl: {
    table: "xl:table",
    head: "xl:table-header-group",
    row: "xl:mb-0 xl:table-row xl:rounded-none xl:border-0 xl:border-b xl:bg-transparent xl:p-0 xl:shadow-none xl:hover:bg-muted/60",
    cell: "xl:table-cell xl:min-w-0 xl:rounded-none xl:bg-transparent xl:px-4 xl:py-4 xl:align-middle",
    label: "xl:hidden",
    value: "xl:contents xl:text-start",
  },
  "2xl": {
    table: "2xl:table",
    head: "2xl:table-header-group",
    row: "2xl:mb-0 2xl:table-row 2xl:rounded-none 2xl:border-0 2xl:border-b 2xl:bg-transparent 2xl:p-0 2xl:shadow-none 2xl:hover:bg-muted/60",
    cell: "2xl:table-cell 2xl:min-w-0 2xl:rounded-none 2xl:bg-transparent 2xl:px-4 2xl:py-4 2xl:align-middle",
    label: "2xl:hidden",
    value: "2xl:contents 2xl:text-start",
  },
};

const TableContext = React.createContext<{
  headers: Array<React.ReactNode>;
  desktopFrom: TableDesktopFrom;
}>({
  headers: [],
  desktopFrom: "xl",
});

function TableRoot({
  headers,
  className,
  children,
  desktopFrom = "xl",
  headerClassName,
  headerCellClassName = "xl:px-4 py-4",
  headerRowClassName,
  ...rest
}: {
  headers: ReactNode[];
  desktopFrom?: TableDesktopFrom;
  headerClassName?: string;
  headerRowClassName?: string;
  headerCellClassName?: string;
} & TableHTMLAttributes<HTMLTableElement>) {
  const responsive = responsiveClasses[desktopFrom];

  return (
    <TableContext.Provider value={{ headers, desktopFrom }}>
      <div className="w-full overflow-x-auto overscroll-x-contain">
        <table
          data-table-root="true"
          className={cn(
            "block w-full min-w-0 border-collapse text-sm leading-6",
            responsive.table,
            className,
          )}
          {...rest}
        >
          <thead
            className={cn(
              "hidden border-b border-border bg-muted/70 text-muted-foreground",
              responsive.head,
              headerClassName,
            )}
          >
            {headers && (
              <tr className={headerRowClassName}>
                {headers.map((header, index) => (
                  <th
                    className={cn(
                      "text-start text-xs font-bold leading-5",
                      headerCellClassName,
                    )}
                    key={index}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          {children}
        </table>
      </div>
    </TableContext.Provider>
  );
}

function Row({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLTableRowElement>) {
  const { desktopFrom } = React.useContext(TableContext);
  const responsive = responsiveClasses[desktopFrom];

  return (
    <tr
      data-table-row="true"
      className={cn(
        "mb-3 block rounded-lg border border-border bg-card px-4 py-2 text-foreground shadow-soft transition-all duration-150 last:mb-0 hover:border-foreground/20",
        responsive.row,
        className,
      )}
      {...rest}
    >
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { index } as any);
        }
        return child;
      })}
    </tr>
  );
}

function Cell({
  className,
  children,
  index,
  ...rest
}: { index?: number } & HTMLAttributes<HTMLTableCellElement>) {
  const { headers, desktopFrom } = React.useContext(TableContext);
  const responsive = responsiveClasses[desktopFrom];

  return (
    <td
      data-table-cell="true"
      className={cn(
        "flex min-w-0 items-start justify-between gap-3 border-b border-border/60 py-3 text-start last:border-b-0",
        responsive.cell,
        className,
      )}
      {...rest}
    >
      <span
        className={cn(
          "w-2/5 shrink-0 text-xs font-bold leading-5 text-muted-foreground",
          responsive.label,
        )}
      >
        {typeof index === "number" ? headers[index] : undefined}
      </span>
      <div
        className={cn("min-w-0 flex-1 break-words text-end", responsive.value)}
      >
        {children}
      </div>
    </td>
  );
}

export const Table = Object.assign(TableRoot, { Row, Cell });
